import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

const VOICE_TEST_ENDPOINT = '/api/aiprompts/dubbing-voice-test';
const VOICE_SAMPLE_ENDPOINT = '/api/aiprompts/dubbing-voice-sample';
const VOICE_TEST_HISTORY_ENDPOINT = '/api/aiprompts/dubbing-voice-test-history';
const GENERATE_ENDPOINT = '/api/aiprompts/dubbing-generate';
const MANIFEST_ENDPOINT = '/api/aiprompts/dubbing-manifest';
const DELETE_ENDPOINT = '/api/aiprompts/dubbing-delete';
const SEPARATE_ENDPOINT = '/api/aiprompts/stems-separate';
const STEMS_MANIFEST_ENDPOINT = '/api/aiprompts/stems-manifest';
const CAPABILITIES_ENDPOINT = '/api/aiprompts/dubbing-capabilities';
const AUTO_TAG_ENDPOINT = '/api/aiprompts/dubbing-auto-tag';

const logger = LogManager.getLogger('repository:DubbingRepository');

function getApiBase(): string {
  return process.env.REACT_APP_API_URL ?? '';
}

/**
 * Typed error thrown when the dubbing API responds non-2xx. Preserves the
 * server's structured `{ error, code }` so callers can map specific codes
 * (`TEXT_TOO_LONG`, `MISSING_CREDENTIALS`, …) to friendly copy. Mirrors
 * `TranslationApiError`.
 */
export class DubbingApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly code?: string;

  constructor(init: {
    context: string;
    status: number;
    statusText: string;
    serverError?: string;
    code?: string;
  }) {
    const detail = init.serverError
      ? init.code
        ? `${init.serverError} (${init.code})`
        : init.serverError
      : '';
    const suffix = detail ? ` — ${detail}` : '';
    super(`${init.context} failed: ${init.status} ${init.statusText}${suffix}`);
    this.name = 'DubbingApiError';
    this.status = init.status;
    this.statusText = init.statusText;
    this.code = init.code;
  }
}

async function throwIfNotOk(
  response: Response,
  context: string,
): Promise<void> {
  if (response.ok) return;
  let serverError: string | undefined;
  let code: string | undefined;
  try {
    const rawBody = await response.text();
    if (rawBody) {
      try {
        const parsed = JSON.parse(rawBody);
        if (parsed && typeof parsed.error === 'string') {
          serverError = parsed.error;
          if (typeof parsed.code === 'string') code = parsed.code;
        } else {
          serverError = rawBody;
        }
      } catch {
        serverError = rawBody;
      }
    }
  } catch {
    // body already consumed or unreadable; fall back to status line
  }
  throw new DubbingApiError({
    context,
    status: response.status,
    statusText: response.statusText,
    serverError,
    code,
  });
}

/**
 * How the lines of a dub run should be delivered (PLAT-11384 / PLAT-11805) —
 * a hand-copied twin of the backend `DubDirection` (`lib/dubbing/direction.ts`).
 * A direction says nothing about the words or the voice, only how a line is
 * read. It is ABSOLUTE: a run with no direction is a direction of "none", never
 * "keep the last one".
 *
 * - `free-form` — the note a director would give ("slower, warmer").
 * - `config` — provider controls set directly, passed through untranslated.
 *   The numeric / on-off panel sends this (PLAT-11797).
 * - `tags` — the line's own words with delivery markers written into them by a
 *   person, e.g. `[whispers] come closer`. Authored by hand rather than
 *   produced from a note, which is why it is its own kind (PLAT-11798).
 *
 *   The marked-up text is an instruction for ONE run. It is never written back
 *   into the translation: the words the dub is faithful to are unchanged.
 * - `tags-by-line` — the same marked-up words for SEVERAL lines, keyed by line
 *   id (PLAT-12198). A run over more than one line sends this; `tags` cannot
 *   say which line its markup is for. A line it leaves out is recorded from
 *   its plain words.
 */
export type DubDirection =
  | { kind: 'free-form'; note: string }
  | { kind: 'config'; settings: Record<string, number | string | boolean> }
  | { kind: 'tags'; taggedText: string }
  | { kind: 'tags-by-line'; taggedTextByLine: Record<string, string> };

/**
 * One way a model can take a direction (backend `DubDirectionMechanism`):
 * `native` — the model takes the note in its own request field; `settings` —
 * the note becomes voice-settings values; `tags` — inline audio-tag markup.
 */
export type DubDirectionMechanism = 'native' | 'settings' | 'tags';

/**
 * How one resolved model interpreted a direction (backend
 * `DubDirectionInterpretationRecord`). One per model in the run.
 */
export interface DubDirectionInterpretationDTO {
  resolvedModelName: string;
  mechanisms: DubDirectionMechanism[];
  applied: {
    native?: { parameter: string; note: string };
    settings?: Record<string, number | string | boolean>;
    tags?: string;
  };
  modelSubstituted?: { from: string; to: string; reason: string };
  unexpressed: string[];
  basedOn?: {
    versionId?: string;
    note?: string;
    controls: Record<string, unknown>;
  };
}

/**
 * A direction a manifest's segments were recorded under (backend
 * `DubDirectionRecord`), keyed by `directionId` in `DubManifestDTO.directions`.
 */
export interface DubDirectionRecordDTO {
  /** The note verbatim, or the config as given — what was asked. */
  direction: DubDirection;
  /** Whitespace-normalized note; the backend's cache-key input. */
  noteNormalized?: string;
  interpretation: DubDirectionInterpretationDTO[];
}

/** Identity snapshot of the handler that produced the audio (backend DTO). */
export interface VoiceTestHandler {
  id: string;
  provider: string;
  modelId?: string;
  modelName?: string;
  version?: string;
}

/** Per-word timing mark within a dub segment (offsets relative to segment start). */
export interface DubWordMark {
  translationWordIndex: number;
  text: string;
  offsetMs: number;
  durationMs: number;
  /** How sure the word aligner is of this word's times, 0..1 (PLAT-11901).
   * Absent unless the word was measured. */
  confidence?: number;
  /**
   * The exact whitespace written after this word in the line's text (PLAT-11901),
   * `''` when nothing follows. Set on every word of a line written by the
   * current splitter; absent on every word of an older line, whose words were
   * split on whitespace and rebuild with single spaces.
   */
  spaceAfter?: string;
}

/** Where a dub segment's word times came from (backend `DubSegmentTiming`,
 * PLAT-11901). Absent on manifests written before it was recorded. */
export interface DubSegmentTimingDTO {
  source: 'estimated' | 'tts' | 'aligned';
  /** aligned: the aligner id; estimated/tts: the voice handler id. */
  handler: string;
  /** aligned: the aligner's model version; estimated/tts: the voice model name. */
  modelVersion: string;
  /** aligned: when first measured; estimated/tts: the line's `producedAt`. */
  alignedAt: string;
}

/** Who produced/last-touched the manifest (backend `DubProvenance`). `name`/
 * `email` are server-resolved and absent on pre-PLAT-10731 dubs. */
export interface DubProvenanceDTO {
  accountId: string;
  orgId?: string;
  name?: string;
  email?: string;
}

/** How the active segments were laid out (backend `DubManifest.alignment`,
 * PLAT-10713/10714). Absent on pre-PLAT-10713 dubs. */
export interface DubAlignmentDTO {
  strategy: 'slide' | 'fit';
  /** Lines Slide pushed past their own original start because an earlier line
   * ran long (drift). Absent on manifests written before this was tracked. */
  slidSegmentIds?: string[];
  /** Uniform global speed-up applied to every line on overflow; `1` = none. */
  globalSpeedFactor: number;
  availableDurationMs: number;
  placedDurationMs: number;
  /** Last-resort lines sped up past the global cap (rare — Slide + a global
   * speed-up is expected to fit). */
  shrunkSegmentIds?: string[];
}

/**
 * One dubbed segment (backend `DubSegment`, PLAT-10590). Audio lives in a shared
 * per-unit rendition WAV referenced by `renditionId`; this segment is the slice
 * `[renditionOffsetMs, renditionOffsetMs + renditionDurationMs)`, placed at
 * `placedStartMs` on the story timeline (PLAT-10713 Slide) — falling back to
 * `originalStartMs` for manifests written before Slide.
 */
export interface DubManifestSegment {
  segmentId: string;
  speaker: string;
  source: 'ai';
  aiVoiceId?: string;
  renditionId: string;
  renditionOffsetMs: number;
  renditionDurationMs: number;
  originalStartMs: number;
  originalEndMs: number;
  /**
   * Where this segment plays on the story timeline (PLAT-10713 Slide). Absent
   * on pre-Slide manifests — consumers fall back to `originalStartMs`.
   */
  placedStartMs?: number;
  words: DubWordMark[];
  /** Where `words[]` times came from (PLAT-11901). Absent on older manifests. */
  timing?: DubSegmentTimingDTO;
  sourceTranslationSnapshot: string;
  /** The handler/provider that voiced this segment. Absent on older manifests. */
  handler?: VoiceTestHandler;
  producedBy?: DubProvenanceDTO;
  producedAt?: string;
  /**
   * The direction this line was recorded under → `DubManifestDTO.directions`
   * (PLAT-11805). Absent when the run that produced it gave no direction.
   */
  directionId?: string;
  /**
   * Why this line is the length it is, and whether it is still with the picture
   * (backend `DubSegmentDiagnostics`, PLAT-11605). Absent on dubs recorded
   * before the backend measured this.
   */
  diagnostics?: DubSegmentDiagnosticsDTO;
}

/**
 * The measurements the backend records for one dubbed line (PLAT-11605), as the
 * dub-info overlay reads them (PLAT-11799). Every field is optional and the
 * whole block is optional — an older dub simply has none.
 */
export interface DubSegmentDiagnosticsDTO {
  /** How long the voice took reading the line unhurried. */
  naturalDurationMs?: number;
  /** The length the engine asked the voice provider for; `0` = it asked for none. */
  targetDurationMs?: number;
  /** The fastest read this line's own model allows. */
  providerMaxSpeedFactor?: number;
  /** The speed-up the engine asked for; `null` when it asked for none. */
  requestedSpeedFactor?: number | null;
  /** The speed-up actually achieved. */
  achievedSpeedFactor?: number;
  /** False when the faster read came back no shorter and was thrown away. */
  speedPassKept?: boolean;
  /** Audio cut off the end as a last resort, in milliseconds. */
  truncatedMs?: number;
  /** Recorded length minus the length asked for — did the provider comply? */
  shortfallMs?: number;
  /** Which pass last re-recorded this line. */
  shortenedBy?: string;
  /**
   * How far past its own picture the line ends, in milliseconds. Negative when
   * it finishes early, and deliberately not clamped — early is information too.
   */
  endLagMs?: number;
}

/**
 * The dub manifest for one language (backend `DubManifest`). Provenance +
 * alignment fields are optional — the backend has always sent them, but they're
 * only consumed by the dub-info overlay (PLAT-10732) and are absent on the
 * oldest manifests.
 */
export interface DubManifestDTO {
  targetLanguage: string;
  sourceLanguage: string;
  segments: DubManifestSegment[];
  createdBy?: DubProvenanceDTO;
  modifiedBy?: DubProvenanceDTO;
  createdAt?: string;
  modifiedAt?: string;
  alignment?: DubAlignmentDTO;
  /**
   * Directions referenced by this manifest's own segments (PLAT-11805), keyed
   * by the segments' `directionId`. Absent entirely — not empty — for an
   * undirected dub, so an older reader sees exactly what it saw before.
   */
  directions?: Record<string, DubDirectionRecordDTO>;
}

/** Manifest + a `renditionId → signed URL` map for playback (PLAT-10619). */
export interface ResolvedDubManifest {
  manifest: DubManifestDTO;
  renditionUrls: Record<string, string>;
}

export interface VoiceTestResult {
  /** Signed URL (1-hour) to the temporary WAV for immediate playback. */
  audioUrl: string;
  durationMs: number;
  format: 'wav';
  handler: VoiceTestHandler;
  /**
   * Id of the persisted audition (PLAT-10579). Present only when the request
   * carried a `showcaseId` (the backend records + returns it); used to prepend
   * the just-completed audition to the history list optimistically.
   */
  auditionId?: string;
  /**
   * PLAT-12023 / PLAT-12028 — the direction the server applied to this
   * synthesis, when the request carried one and the server honours it. Absent
   * from a server that does not apply directions to previews yet, which is how
   * the client tells an undirected reply apart from a directed one.
   */
  direction?: DubDirectionRecordDTO;
}

/**
 * One entry in a workspace's audition history (PLAT-10579/PLAT-10580). A
 * durable record of a past ad-hoc audition, replayable via its `audioUrl`
 * without re-synthesis. `audioUrl` is `null` when the stored object has aged
 * out (DI14 lifecycle) — the UI shows "no longer available" instead of a
 * broken player.
 */
export interface AuditionHistoryEntry {
  auditionId: string;
  aiVoiceId: string;
  provider: string;
  language: string;
  text: string;
  durationMs: number;
  /** ISO-8601 timestamp of when the audition was taken. */
  createdAt: string;
  audioUrl: string | null;
  /**
   * Who ran the audition — server-resolved provenance (name, else email),
   * `null` for anonymous / unresolved callers. Never trusted from the client.
   */
  createdByName: string | null;
}

/** A pre-saved per-language sample (DE8): a signed URL to the stored asset. */
export interface VoiceSampleResult {
  audioUrl: string;
  /** Duration of the stored sample, when the backend reports it. */
  durationMs?: number;
}

/**
 * Parameters for a dub-generate run (PLAT-10596 / T8), the frontend twin of the
 * backend `runDubGenerate` input (PLAT-10590). `voiceBySpeaker` is the resolved
 * speaker→AI-Voice map — keyed by the transcript's internal `spk_X` id (what the
 * manifest segments key on), resolved by the kick-off popover per DI19. The
 * engine does no default resolution; it dubs exactly what this map says.
 */
export interface GenerateDubParams {
  storyId: string;
  targetLanguage: string;
  /** Source (transcript) language; defaults server-side to the translation's. */
  sourceLanguage?: string;
  /** Segments to ensure-dubbed; omit = the whole saved translation (M1 span). */
  segmentIds?: string[];
  /** Resolved `spk_X → aiVoiceId` map. Required, non-empty. */
  voiceBySpeaker: Record<string, string>;
  /** Wholesale redo — re-synthesize every requested segment (Redub). */
  regenerate?: boolean;
  /**
   * How much room the dub has on the story's timeline, in milliseconds. Sent as
   * the engine's `availableDurationMs` override; omit to keep its default.
   *
   * The engine's own default is the end of the last spoken word, which a
   * translation always overruns, because a dubbed line is longer than the line
   * it replaces. Every run after the first therefore reads as an overflow and
   * answers it by speeding up and re-recording every line of the story. Sending
   * the source video's true length removes that false overflow.
   *
   * An absolute ceiling on the story's timeline, never a clip length: the engine
   * compares it against the end of the last placed line across the whole story,
   * so a clip-sized number reads as a worse overflow than no value at all.
   */
  availableDurationMs?: number;
  /**
   * How the lines should be delivered (PLAT-11384). Omit for an undirected run;
   * never send an empty note — the backend treats a blank note as no direction,
   * and a 400 names any direction the resolved model cannot take.
   */
  direction?: DubDirection;
  signal?: AbortSignal;
}

/**
 * What one TTS model can be asked to do (PLAT-11385), one row of
 * `GET /api/aiprompts/dubbing-capabilities`. A hand-copied twin of the backend
 * `DubModelCapability` plus the derived `mechanisms` list the route adds.
 * `declared: false` marks the unknown-model default rather than a real
 * declaration — same behaviour, different sentence to a person.
 */
export interface DubModelCapabilityDTO {
  /** The provider's own model string, e.g. `'tts-1'` — the lookup key. */
  modelId: string;
  provider: string;
  declared: boolean;
  /** Every mechanism the model supports; empty = cannot be directed. */
  mechanisms: DubDirectionMechanism[];
  /** The request field a free-form note is passed in (`native` mechanism). */
  directionParameter?: string;
  speed: { supported: boolean; min: number; max: number };
  /**
   * `knobs` names the numeric / on-off controls this model exposes.
   * `knobDeclarations` gives each one's type and, for a number, its lowest and
   * highest declared value (PLAT-11797). The panel draws its fields from the
   * two together, so the screen and the backend validate against the SAME
   * declaration and nothing hardcodes a provider's numbers.
   *
   * Partial on purpose: a control the backend has not looked the limits up for
   * is still accepted by name, so it is listed rather than drawn.
   */
  voiceSettings: {
    supported: boolean;
    knobs: string[];
    knobDeclarations?: Record<string, DubKnobDeclarationDTO>;
  };
  /**
   * `markers` lists the delivery markers the model documents (PLAT-12030),
   * grouped by what they change; absent from a server that does not carry
   * them yet, in which case the studio's own list stands in.
   */
  audioTags: { supported: boolean; markers?: DubMarkerDeclarationDTO[] };
}

/** One documented delivery marker (backend `DubMarkerDeclaration`, PLAT-12030). */
export interface DubMarkerDeclarationDTO {
  /** As written inside the brackets: `whispers`. */
  tag: string;
  kind: 'emotion' | 'pacing' | 'delivery';
}

/**
 * The words a person reads for a control (backend `DubKnobWords`,
 * PLAT-12030), beside its range. All optional: a row from a server that does
 * not carry them yet, or a control nobody has written up, falls back to the
 * studio's own words.
 */
export interface DubKnobWordsDTO {
  label?: string;
  ends?: { low: string; high: string };
  effect?: string;
}

/**
 * What ONE numeric / on-off voice control accepts (backend `DubKnobDeclaration`).
 * Two shapes only, because only two are declared by any model served today: a
 * continuous number with both bounds, and a plain on/off.
 *
 * `default` is the value the model uses when the control is not sent. It is the
 * panel's starting position when there is no previous run to start from.
 */
export type DubKnobDeclarationDTO =
  | ({
      type: 'number';
      min: number;
      max: number;
      default?: number;
    } & DubKnobWordsDTO)
  | ({ type: 'boolean'; default?: boolean } & DubKnobWordsDTO);

/** Result of `POST /api/aiprompts/dubbing-auto-tag` (PLAT-12029). */
export interface AutoTagResult {
  /** The line with markers written in; the words unchanged. */
  taggedText: string;
  /** The markers used, in order, as written inside the brackets. */
  markers: string[];
  modelId: string;
}

/** Result of `GET /api/aiprompts/dubbing-capabilities?modelIds=a,b`. */
export interface DubCapabilitiesResult {
  models: DubModelCapabilityDTO[];
}

/** Coverage report of a dub-generate run (backend `DubGenerateResult.coverage`). */
export interface DubGenerateCoverage {
  requested: string[];
  synthesized: string[];
  reused: string[];
  /** Segments whose synthesis unit failed (reported, not fatal). */
  failed: { segmentId: string; error: string }[];
  /** Segments not dubbed (no voice / no timing / unknown segment). */
  skipped: { segmentId: string; reason: string }[];
}

/** Result of `POST /api/aiprompts/dubbing-generate` (backend `DubGenerateResult`). */
export interface DubGenerateResult {
  runId: string;
  targetLanguage: string;
  sourceLanguage: string;
  coverage: DubGenerateCoverage;
  /** Representative handler; absent when nothing was synthesized. */
  handler?: VoiceTestHandler;
}

/**
 * Result of `POST /api/aiprompts/dubbing-delete` (backend `DubDeleteResult`,
 * PLAT-10591). A soft delete: the affected segments are tombstoned (renditions
 * survive, DI14); `active` is the remaining active-segment count.
 */
export interface DubDeleteResult {
  runId: string;
  targetLanguage: string;
  kind: 'delete' | 'restore';
  affected: string[];
  skipped: { segmentId: string; reason: string }[];
  active: number;
}

/**
 * Params for a voice/music separation run (PLAT-10622), the frontend twin of
 * the backend `runStemSeparate` input (PLAT-10623). `handlerInternalName`
 * carries the launch-period splitter override; omit for the backend env
 * default (server-chosen — the frontend does not assume which). `regenerate`
 * re-runs even if cached.
 */
export interface SeparateStemsParams {
  storyId: string;
  handlerInternalName?: string;
  regenerate?: boolean;
  signal?: AbortSignal;
}

/** Result of `POST /api/aiprompts/stems-separate` (backend `StemSeparateResult`). */
export interface StemSeparateResult {
  variantId: string;
  splitType: string;
  /** Per-stem component tags (`[['voice'], ['music','noise']]`). */
  components: string[][];
  /** The kept non-voice bed is near-silent ("No music found"). */
  noMusic: boolean;
  /** True when an existing variant was returned without re-running the splitter. */
  cached: boolean;
  handler: { id: string; provider: string };
}

/**
 * A story's current stems state (backend `StemsSummary`, PLAT-10622 DS2). Drives
 * the Separate ↔ Re-separate label + the "No music found" note. `hasStems:false`
 * is the normal "not separated yet" state.
 */
export interface StemsSummary {
  hasStems: boolean;
  noMusic: boolean;
  variantId?: string;
  splitType?: string;
  handler?: { id: string; provider: string };
  /**
   * Signed URL of the kept non-voice bed stem, so it can play under the dub
   * (PLAT-10626), MP3-preferred. Present only when a music-bearing bed exists
   * (`hasStems && !noMusic`); absent otherwise.
   */
  bedUrl?: string;
  /**
   * Per-format download URLs for the bed / voice stems (PLAT-10715): compact MP3
   * derivative + lossless WAV master. `*Mp3` present only when the eager transcode
   * produced it; `*Wav` whenever the master exists.
   */
  bedUrlMp3?: string;
  bedUrlWav?: string;
  voiceUrlMp3?: string;
  voiceUrlWav?: string;
  /**
   * Signed URL of the un-separated source audio, for download/QA (PLAT-10627).
   * Single native format (PLAT-10715).
   */
  originalUrl?: string;
  /**
   * Byte size of each downloadable track, so the download menu can show the file
   * size next to its format (PLAT-10715). Present alongside the matching URL.
   */
  bedBytesMp3?: number;
  bedBytesWav?: number;
  voiceBytesMp3?: number;
  voiceBytesWav?: number;
  originalBytes?: number;
}

function ensureStemSeparateResult(
  value: unknown,
  context: string,
): StemSeparateResult {
  if (
    !value ||
    typeof value !== 'object' ||
    typeof (value as { variantId?: unknown }).variantId !== 'string'
  ) {
    throw new Error(`${context}: response missing variantId`);
  }
  const v = value as {
    variantId: string;
    splitType?: unknown;
    components?: unknown;
    noMusic?: unknown;
    cached?: unknown;
    handler?: unknown;
  };
  return {
    variantId: v.variantId,
    splitType: typeof v.splitType === 'string' ? v.splitType : '',
    components: Array.isArray(v.components)
      ? v.components
          .filter((c): c is unknown[] => Array.isArray(c))
          .map(c => c.filter((x): x is string => typeof x === 'string'))
      : [],
    noMusic: v.noMusic === true,
    cached: v.cached === true,
    handler:
      v.handler && typeof v.handler === 'object'
        ? (v.handler as { id: string; provider: string })
        : { id: 'unknown', provider: 'unknown' },
  };
}

function ensureStemsSummary(value: unknown): StemsSummary {
  if (!value || typeof value !== 'object') {
    return { hasStems: false, noMusic: false };
  }
  const v = value as {
    hasStems?: unknown;
    noMusic?: unknown;
    variantId?: unknown;
    splitType?: unknown;
    handler?: unknown;
    bedUrl?: unknown;
    bedUrlMp3?: unknown;
    bedUrlWav?: unknown;
    voiceUrlMp3?: unknown;
    voiceUrlWav?: unknown;
    originalUrl?: unknown;
    bedBytesMp3?: unknown;
    bedBytesWav?: unknown;
    voiceBytesMp3?: unknown;
    voiceBytesWav?: unknown;
    originalBytes?: unknown;
  };
  const num = (x: unknown): x is number => typeof x === 'number';
  return {
    hasStems: v.hasStems === true,
    noMusic: v.noMusic === true,
    ...(typeof v.variantId === 'string' ? { variantId: v.variantId } : {}),
    ...(typeof v.splitType === 'string' ? { splitType: v.splitType } : {}),
    ...(v.handler && typeof v.handler === 'object'
      ? { handler: v.handler as { id: string; provider: string } }
      : {}),
    ...(typeof v.bedUrl === 'string' ? { bedUrl: v.bedUrl } : {}),
    ...(typeof v.bedUrlMp3 === 'string' ? { bedUrlMp3: v.bedUrlMp3 } : {}),
    ...(typeof v.bedUrlWav === 'string' ? { bedUrlWav: v.bedUrlWav } : {}),
    ...(typeof v.voiceUrlMp3 === 'string'
      ? { voiceUrlMp3: v.voiceUrlMp3 }
      : {}),
    ...(typeof v.voiceUrlWav === 'string'
      ? { voiceUrlWav: v.voiceUrlWav }
      : {}),
    ...(typeof v.originalUrl === 'string'
      ? { originalUrl: v.originalUrl }
      : {}),
    ...(num(v.bedBytesMp3) ? { bedBytesMp3: v.bedBytesMp3 } : {}),
    ...(num(v.bedBytesWav) ? { bedBytesWav: v.bedBytesWav } : {}),
    ...(num(v.voiceBytesMp3) ? { voiceBytesMp3: v.voiceBytesMp3 } : {}),
    ...(num(v.voiceBytesWav) ? { voiceBytesWav: v.voiceBytesWav } : {}),
    ...(num(v.originalBytes) ? { originalBytes: v.originalBytes } : {}),
  };
}

function ensureVoiceTestResult(
  value: unknown,
  context: string,
): VoiceTestResult {
  if (
    !value ||
    typeof value !== 'object' ||
    typeof (value as { audioUrl?: unknown }).audioUrl !== 'string'
  ) {
    throw new Error(`${context}: response missing audioUrl`);
  }
  // `audioUrl` is the only field we hard-require; the rest are typed non-null
  // but defaulted defensively so a partial backend response can't leak
  // `undefined` into the download filename (`format`) or analytics
  // (`durationMs`). Mirrors the defensive parsing in `getVoiceSampleUrl`.
  const v = value as {
    audioUrl: string;
    durationMs?: unknown;
    format?: unknown;
    handler?: unknown;
    auditionId?: unknown;
    direction?: unknown;
  };
  return {
    audioUrl: v.audioUrl,
    durationMs: typeof v.durationMs === 'number' ? v.durationMs : 0,
    format: 'wav',
    handler:
      v.handler && typeof v.handler === 'object'
        ? (v.handler as VoiceTestHandler)
        : { id: 'unknown', provider: 'unknown' },
    ...(typeof v.auditionId === 'string' ? { auditionId: v.auditionId } : {}),
    ...(v.direction && typeof v.direction === 'object'
      ? { direction: v.direction as DubDirectionRecordDTO }
      : {}),
  };
}

/**
 * Defensively narrow a backend history payload to `AuditionHistoryEntry[]`.
 * Skips malformed rows rather than throwing, and coerces a missing/absent
 * `audioUrl` to `null` (the aged-out state). Mirrors the defensive parsing in
 * `ensureVoiceTestResult`.
 */
function ensureAuditionHistory(
  value: unknown,
  context: string,
): AuditionHistoryEntry[] {
  const auditions = (value as { auditions?: unknown })?.auditions;
  if (!Array.isArray(auditions)) {
    throw new Error(`${context}: response missing auditions array`);
  }
  return auditions.flatMap(raw => {
    if (!raw || typeof raw !== 'object') return [];
    const r = raw as Record<string, unknown>;
    if (typeof r.auditionId !== 'string' || typeof r.aiVoiceId !== 'string') {
      return [];
    }
    // Provenance shape: { createdBy: { actor: { name, email, … } } }. Prefer the
    // resolved name, fall back to email, else null (anonymous / unresolved).
    const createdBy =
      r.createdBy && typeof r.createdBy === 'object'
        ? (r.createdBy as { actor?: unknown }).actor
        : undefined;
    const actor =
      createdBy && typeof createdBy === 'object'
        ? (createdBy as Record<string, unknown>)
        : undefined;
    const createdByName =
      (typeof actor?.name === 'string' && actor.name) ||
      (typeof actor?.email === 'string' && actor.email) ||
      null;
    return [
      {
        auditionId: r.auditionId,
        aiVoiceId: r.aiVoiceId,
        provider: typeof r.provider === 'string' ? r.provider : 'unknown',
        language: typeof r.language === 'string' ? r.language : '',
        text: typeof r.text === 'string' ? r.text : '',
        durationMs: typeof r.durationMs === 'number' ? r.durationMs : 0,
        createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
        audioUrl: typeof r.audioUrl === 'string' ? r.audioUrl : null,
        createdByName,
      },
    ];
  });
}

/** Narrow an `{ segmentId, <key> }[]` payload defensively, dropping bad rows. */
function ensureCoverageDetail<K extends string>(
  value: unknown,
  key: K,
): ({ segmentId: string } & Record<K, string>)[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(raw => {
    if (!raw || typeof raw !== 'object') return [];
    const r = raw as Record<string, unknown>;
    if (typeof r.segmentId !== 'string') return [];
    return [
      {
        segmentId: r.segmentId,
        [key]: typeof r[key] === 'string' ? (r[key] as string) : '',
      } as { segmentId: string } & Record<K, string>,
    ];
  });
}

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];

/**
 * Defensively narrow a backend dub-generate payload to `DubGenerateResult`.
 * Requires a `runId`; coverage arrays default to empty so a partial response
 * can't leak `undefined` into the coverage summary. Mirrors the defensive
 * parsing of `ensureVoiceTestResult` / `ensureAuditionHistory`.
 */
function ensureDubGenerateResult(
  value: unknown,
  context: string,
): DubGenerateResult {
  if (
    !value ||
    typeof value !== 'object' ||
    typeof (value as { runId?: unknown }).runId !== 'string'
  ) {
    throw new Error(`${context}: response missing runId`);
  }
  const v = value as {
    runId: string;
    targetLanguage?: unknown;
    sourceLanguage?: unknown;
    coverage?: unknown;
    handler?: unknown;
  };
  const coverage = (v.coverage ?? {}) as Record<string, unknown>;
  return {
    runId: v.runId,
    targetLanguage:
      typeof v.targetLanguage === 'string' ? v.targetLanguage : '',
    sourceLanguage:
      typeof v.sourceLanguage === 'string' ? v.sourceLanguage : '',
    coverage: {
      requested: asStringArray(coverage.requested),
      synthesized: asStringArray(coverage.synthesized),
      reused: asStringArray(coverage.reused),
      failed: ensureCoverageDetail(coverage.failed, 'error'),
      skipped: ensureCoverageDetail(coverage.skipped, 'reason'),
    },
    ...(v.handler && typeof v.handler === 'object'
      ? { handler: v.handler as VoiceTestHandler }
      : {}),
  };
}

/**
 * Defensively narrow a backend dub-delete payload to `DubDeleteResult`.
 * Requires a `runId`; the rest default so a partial response can't leak
 * `undefined`. The frontend only needs the call to have succeeded (the host
 * drives its own state cleanup), so the guard is intentionally light. Mirrors
 * `ensureDubGenerateResult`.
 */
function ensureDubDeleteResult(
  value: unknown,
  context: string,
): DubDeleteResult {
  if (
    !value ||
    typeof value !== 'object' ||
    typeof (value as { runId?: unknown }).runId !== 'string'
  ) {
    throw new Error(`${context}: response missing runId`);
  }
  const v = value as {
    runId: string;
    targetLanguage?: unknown;
    kind?: unknown;
    affected?: unknown;
    skipped?: unknown;
    active?: unknown;
  };
  return {
    runId: v.runId,
    targetLanguage:
      typeof v.targetLanguage === 'string' ? v.targetLanguage : '',
    kind: v.kind === 'restore' ? 'restore' : 'delete',
    affected: asStringArray(v.affected),
    skipped: ensureCoverageDetail(v.skipped, 'reason'),
    active: typeof v.active === 'number' ? v.active : 0,
  };
}

const isObjectRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const MECHANISM_NAMES = new Set<string>(['native', 'settings', 'tags']);
const isMechanism = (v: unknown): v is DubDirectionMechanism =>
  typeof v === 'string' && MECHANISM_NAMES.has(v);

/**
 * Defensively narrow the per-control declarations (PLAT-11797). A control whose
 * row is not a complete, usable declaration is DROPPED rather than half-read:
 * the panel would otherwise draw a slider with an invented range, and the
 * backend would refuse what it produced. Returns `undefined` when nothing
 * usable came back, so the field stays absent exactly as the backend sends it.
 */
function ensureKnobDeclarations(
  value: unknown,
): Record<string, DubKnobDeclarationDTO> | undefined {
  if (!isObjectRecord(value)) return undefined;
  const out: Record<string, DubKnobDeclarationDTO> = {};
  for (const [name, raw] of Object.entries(value)) {
    if (!isObjectRecord(raw)) continue;
    if (raw.type === 'number') {
      if (typeof raw.min !== 'number' || typeof raw.max !== 'number') continue;
      if (raw.min > raw.max) continue;
      out[name] = {
        type: 'number',
        min: raw.min,
        max: raw.max,
        ...(typeof raw.default === 'number' ? { default: raw.default } : {}),
        ...ensureKnobWords(raw),
      };
    } else if (raw.type === 'boolean') {
      out[name] = {
        type: 'boolean',
        ...(typeof raw.default === 'boolean' ? { default: raw.default } : {}),
        ...ensureKnobWords(raw),
      };
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The words a control declaration carries (PLAT-12030); each kept only when well-formed. */
function ensureKnobWords(raw: Record<string, unknown>): DubKnobWordsDTO {
  const ends = isObjectRecord(raw.ends) ? raw.ends : null;
  return {
    ...(typeof raw.label === 'string' ? { label: raw.label } : {}),
    ...(ends && typeof ends.low === 'string' && typeof ends.high === 'string'
      ? { ends: { low: ends.low, high: ends.high } }
      : {}),
    ...(typeof raw.effect === 'string' ? { effect: raw.effect } : {}),
  };
}

const isMarkerKind = (v: unknown): v is DubMarkerDeclarationDTO['kind'] =>
  v === 'emotion' || v === 'pacing' || v === 'delivery';

/**
 * The delivery markers a model documents (PLAT-12030). Absent when none came
 * back well-formed, so the studio's own list stands in.
 */
function ensureMarkerDeclarations(
  value: unknown,
): DubMarkerDeclarationDTO[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.flatMap(raw =>
    isObjectRecord(raw) && typeof raw.tag === 'string' && isMarkerKind(raw.kind)
      ? [{ tag: raw.tag, kind: raw.kind }]
      : [],
  );
  return out.length > 0 ? out : undefined;
}

/**
 * Defensively narrow a backend capabilities payload (PLAT-11385) to
 * `DubCapabilitiesResult`. Rows without a string `modelId` are dropped; every
 * other field defaults to "cannot" so a partial row can never read as a model
 * that takes a direction it does not. Mirrors `ensureAuditionHistory`.
 */
function ensureDubCapabilities(
  value: unknown,
  context: string,
): DubCapabilitiesResult {
  const models = isObjectRecord(value) ? value.models : undefined;
  if (!Array.isArray(models)) {
    throw new Error(`${context}: response missing models array`);
  }
  return {
    models: models.flatMap(raw => {
      if (!isObjectRecord(raw) || typeof raw.modelId !== 'string') return [];
      const speed = isObjectRecord(raw.speed) ? raw.speed : {};
      const voiceSettings = isObjectRecord(raw.voiceSettings)
        ? raw.voiceSettings
        : {};
      const audioTags = isObjectRecord(raw.audioTags) ? raw.audioTags : {};
      return [
        {
          modelId: raw.modelId,
          provider: typeof raw.provider === 'string' ? raw.provider : 'unknown',
          declared: raw.declared === true,
          mechanisms: Array.isArray(raw.mechanisms)
            ? raw.mechanisms.filter(isMechanism)
            : [],
          ...(typeof raw.directionParameter === 'string'
            ? { directionParameter: raw.directionParameter }
            : {}),
          speed: {
            supported: speed.supported === true,
            min: typeof speed.min === 'number' ? speed.min : 1,
            max: typeof speed.max === 'number' ? speed.max : 1,
          },
          voiceSettings: {
            supported: voiceSettings.supported === true,
            knobs: asStringArray(voiceSettings.knobs),
            ...(() => {
              const declared = ensureKnobDeclarations(
                voiceSettings.knobDeclarations,
              );
              return declared ? { knobDeclarations: declared } : {};
            })(),
          },
          audioTags: {
            supported: audioTags.supported === true,
            ...(() => {
              const markers = ensureMarkerDeclarations(audioTags.markers);
              return markers ? { markers } : {};
            })(),
          },
        },
      ];
    }),
  };
}

/**
 * REST caller for the dubbing backend (arbor-v1 `aiprompts`). The dubbing twin
 * of `TranslationRepository`: pure `fetch` over `REACT_APP_API_URL`, no Dato /
 * GraphQL client. Hosts the ad-hoc voice test (DE9) and the dub-generate
 * kick-off (PLAT-10482 T2/T8) + the dub soft-delete (`dubbing-delete`, T14).
 */
export class DubbingRepository {
  /**
   * Ad-hoc voice test (DE9 / PLAT-10496): synthesize an arbitrary phrase with
   * an AI Voice and get back a temporary audio URL for immediate playback.
   * Never touches a saved translation or a catalog sample. `language` is a
   * BCP-47 code (e.g. `es-ES`) — it becomes an S3 key segment server-side.
   */
  async voiceTest(params: {
    aiVoiceId: string;
    language: string;
    text: string;
    /**
     * Workspace the audition belongs to (PLAT-10579). When present the backend
     * persists a durable audition record and returns its `auditionId`; when
     * omitted the audition stays a throwaway (legacy behaviour).
     */
    showcaseId?: string;
    /**
     * PLAT-12023 — how the words should be delivered, for a directed preview.
     * The same shape a dub run sends; the server applies it once PLAT-12028
     * is deployed and reports it back on the result.
     */
    direction?: DubDirection;
    signal?: AbortSignal;
  }): Promise<VoiceTestResult> {
    const { signal, ...body } = params;
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${VOICE_TEST_ENDPOINT}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        },
      );
      await throwIfNotOk(response, 'DubbingRepository.voiceTest');
      const data = (await response.json()) as unknown;
      return ensureVoiceTestResult(data, 'DubbingRepository.voiceTest');
    } catch (error) {
      // AbortError is a user action (unmount / re-audition), not a failure.
      if (isAbortError(error)) throw error;
      logger.error('voiceTest failed', {
        aiVoiceId: params.aiVoiceId,
        language: params.language,
        textLength: params.text.length,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Kick off a dub for a story's target language (PLAT-10596 / T8): synthesize
   * per-segment audio from the saved translation using the resolved
   * `voiceBySpeaker` map. Additive by default (only uncovered segments are
   * synthesized; the rest are reused); `regenerate: true` forces a wholesale
   * redo. Omitting `segmentIds` dubs the whole saved translation (the M1 span).
   * Returns the run's coverage report. Same REST idiom as `voiceTest` — typed
   * `DubbingApiError`, abort-aware, `extractErrorInfo` log.
   */
  async generateDub(params: GenerateDubParams): Promise<DubGenerateResult> {
    const { signal, ...body } = params;
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${GENERATE_ENDPOINT}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        },
      );
      await throwIfNotOk(response, 'DubbingRepository.generateDub');
      const data = (await response.json()) as unknown;
      return ensureDubGenerateResult(data, 'DubbingRepository.generateDub');
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('generateDub failed', {
        storyId: params.storyId,
        targetLanguage: params.targetLanguage,
        speakerCount: Object.keys(params.voiceBySpeaker).length,
        regenerate: params.regenerate ?? false,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Delete a story's dub for a language (PLAT-10617 / T14 — the frontend
   * counterpart to the backend soft-delete, PLAT-10591). Whole-language delete:
   * `segmentIds` is omitted, so the backend tombstones every active segment.
   * The delete is **non-destructive** (DI14) — segments move to a tombstone
   * list, renditions survive, so a later re-dub reuses them. A `404` /
   * `DUB_NOT_FOUND` means there is no dub to delete — an expected already-gone
   * state → `null` (the caller still clears its local state), not a logged
   * error, mirroring `getDubManifest`. Other non-2xx throw a typed
   * `DubbingApiError`. Same REST idiom as `generateDub`.
   */
  async deleteDub(params: {
    storyId: string;
    targetLanguage: string;
    signal?: AbortSignal;
  }): Promise<DubDeleteResult | null> {
    const { signal, ...body } = params;
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${DELETE_ENDPOINT}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        },
      );
      if (response.status === 404) return null;
      await throwIfNotOk(response, 'DubbingRepository.deleteDub');
      const data = (await response.json()) as unknown;
      return ensureDubDeleteResult(data, 'DubbingRepository.deleteDub');
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (error instanceof DubbingApiError && error.code === 'DUB_NOT_FOUND') {
        return null;
      }
      logger.error('deleteDub failed', {
        storyId: params.storyId,
        targetLanguage: params.targetLanguage,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Kick off voice/music separation for a story (PLAT-10622 / PLAT-10623):
   * split the source audio into component-tagged stems so the kept music bed
   * can play under the dub. `handlerInternalName` picks the splitter (launch
   * override, DE4); omit for the env default. `regenerate` forces a re-run even
   * if a matching `stems` variant is cached. The call is **long-running**
   * (upload → poll can take minutes); callers show a spinner and pass a
   * `signal` for unmount. Same REST idiom as `generateDub` — typed
   * `DubbingApiError`, abort-aware, `extractErrorInfo` log.
   */
  async separateStems(
    params: SeparateStemsParams,
  ): Promise<StemSeparateResult> {
    const { signal, ...body } = params;
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${SEPARATE_ENDPOINT}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        },
      );
      await throwIfNotOk(response, 'DubbingRepository.separateStems');
      const data = (await response.json()) as unknown;
      return ensureStemSeparateResult(data, 'DubbingRepository.separateStems');
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('separateStems failed', {
        storyId: params.storyId,
        handlerInternalName: params.handlerInternalName,
        regenerate: params.regenerate ?? false,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Read a story's current stems state (PLAT-10622 DS2) — powers the Separate ↔
   * Re-separate label + the "No music found" note. "Not separated yet" is the
   * normal `{ hasStems: false }` state (200), so this never returns `null`; any
   * failure degrades to `{ hasStems: false }` (the control just offers Separate).
   */
  async getStemsSummary(params: {
    storyId: string;
    signal?: AbortSignal;
  }): Promise<StemsSummary> {
    const { storyId, signal } = params;
    const query = new URLSearchParams({ storyId });
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${STEMS_MANIFEST_ENDPOINT}?${query}`,
        { method: 'GET', signal, cache: 'no-store' },
      );
      await throwIfNotOk(response, 'DubbingRepository.getStemsSummary');
      const data = (await response.json()) as unknown;
      return ensureStemsSummary(data);
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('getStemsSummary failed', {
        storyId,
        ...extractErrorInfo(error),
      });
      return { hasStems: false, noMusic: false };
    }
  }

  /**
   * Pre-saved per-language sample (DE8): fetch the signed URL of the voice's
   * stored `(voice, language)` sample (the voice reading its description in
   * that language). Unlike `voiceTest` (DE9), this never synthesizes — it only
   * serves a stored asset. A `404` / `SAMPLE_UNAVAILABLE` is an **expected**
   * "not generated yet" state and resolves to `null` (the caller shows "Sample
   * unavailable"), not an error — so it is not logged. Other non-2xx throw a
   * typed `DubbingApiError`.
   *
   * NOTE: the backend endpoint (DE8 sample generation + serving) is not built
   * yet; until it exists every call resolves to `null` and the UI degrades to
   * "Sample unavailable".
   */
  async getVoiceSampleUrl(params: {
    aiVoiceId: string;
    language: string;
    signal?: AbortSignal;
  }): Promise<VoiceSampleResult | null> {
    const { aiVoiceId, language, signal } = params;
    const query = new URLSearchParams({ aiVoiceId, language });
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${VOICE_SAMPLE_ENDPOINT}?${query}`,
        { method: 'GET', signal },
      );
      // No sample generated yet for this (voice, language) — expected, not an
      // error. Surfaced to the user as "Sample unavailable".
      if (response.status === 404) return null;
      await throwIfNotOk(response, 'DubbingRepository.getVoiceSampleUrl');
      const data = (await response.json()) as unknown;
      if (
        !data ||
        typeof data !== 'object' ||
        typeof (data as { audioUrl?: unknown }).audioUrl !== 'string'
      ) {
        return null;
      }
      const { audioUrl, durationMs } = data as VoiceSampleResult;
      return typeof durationMs === 'number'
        ? { audioUrl, durationMs }
        : { audioUrl };
    } catch (error) {
      if (isAbortError(error)) throw error;
      // A structured SAMPLE_UNAVAILABLE code (non-404 transport) is still the
      // expected empty state, not a failure to log.
      if (
        error instanceof DubbingApiError &&
        error.code === 'SAMPLE_UNAVAILABLE'
      ) {
        return null;
      }
      logger.error('getVoiceSampleUrl failed', {
        aiVoiceId,
        language,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * List a workspace's audition history (PLAT-10579/PLAT-10580): the past
   * ad-hoc auditions, newest first, each with a fresh signed replay URL (or
   * `null` audioUrl for an aged-out object). Replaying a returned entry streams
   * the stored bytes — nothing is re-synthesized. Same REST idiom as
   * `voiceTest` (typed `DubbingApiError`, abort-aware, `extractErrorInfo` log).
   */
  async listVoiceTests(params: {
    showcaseId: string;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<AuditionHistoryEntry[]> {
    const { showcaseId, limit, signal } = params;
    const query = new URLSearchParams({ showcaseId });
    if (limit !== undefined) query.set('limit', String(limit));
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${VOICE_TEST_HISTORY_ENDPOINT}?${query}`,
        { method: 'GET', signal },
      );
      await throwIfNotOk(response, 'DubbingRepository.listVoiceTests');
      const data = (await response.json()) as unknown;
      return ensureAuditionHistory(data, 'DubbingRepository.listVoiceTests');
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('listVoiceTests failed', {
        showcaseId,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * Read a story's dub manifest for a language + the signed URLs to play it
   * (PLAT-10619). A `404` / `DUB_NOT_FOUND` means no dub exists yet — an
   * expected empty state → `null` (the surface shows a generate prompt), not a
   * logged error. Other non-2xx throw a typed `DubbingApiError`.
   */
  async getDubManifest(params: {
    storyId: string;
    language: string;
    signal?: AbortSignal;
  }): Promise<ResolvedDubManifest | null> {
    const { storyId, language, signal } = params;
    const query = new URLSearchParams({ storyId, language });
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${MANIFEST_ENDPOINT}?${query}`,
        // `cache: 'no-store'` so a re-fetch after a re-dub (PLAT-10616's
        // reloadToken) gets the FRESH manifest. The manifest read is fresh
        // server-side, but the endpoint sends no Cache-Control, so a plain GET
        // can be served from the browser cache — returning the stale manifest
        // (old renditionIds) and keeping the old audio until a hard reload.
        { method: 'GET', signal, cache: 'no-store' },
      );
      if (response.status === 404) return null;
      await throwIfNotOk(response, 'DubbingRepository.getDubManifest');
      const data = (await response.json()) as unknown;
      if (
        !data ||
        typeof data !== 'object' ||
        !(data as { manifest?: unknown }).manifest ||
        typeof (data as { renditionUrls?: unknown }).renditionUrls !== 'object'
      ) {
        return null;
      }
      return data as ResolvedDubManifest;
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (error instanceof DubbingApiError && error.code === 'DUB_NOT_FOUND') {
        return null;
      }
      logger.error('getDubManifest failed', {
        storyId,
        language,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  /**
   * List the languages that have a dub for a story (PLAT-10619) — powers the
   * "green mic" indicator in the language dropdown (PLAT-10621). Returns `[]`
   * on any failure (the indicator is advisory, never blocks the dropdown).
   */
  async listDubbedLanguages(params: {
    storyId: string;
    signal?: AbortSignal;
  }): Promise<string[]> {
    const { storyId, signal } = params;
    const query = new URLSearchParams({ storyId });
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${MANIFEST_ENDPOINT}?${query}`,
        // `cache: 'no-store'`, as the per-language read above (PLAT-11704): the
        // endpoint sends no Cache-Control, so a plain GET could answer from
        // the browser cache with the list of a previous visit — and a language
        // that has since been dubbed or un-dubbed would read wrong until a
        // hard reload.
        { method: 'GET', signal, cache: 'no-store' },
      );
      await throwIfNotOk(response, 'DubbingRepository.listDubbedLanguages');
      const data = (await response.json()) as unknown;
      const langs = (data as { languages?: unknown })?.languages;
      return Array.isArray(langs)
        ? langs.filter((l): l is string => typeof l === 'string')
        : [];
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('listDubbedLanguages failed', {
        storyId,
        ...extractErrorInfo(error),
      });
      return [];
    }
  }

  /**
   * What each of the given models can be asked to do (PLAT-11385): the
   * mechanisms it declares for a direction, its speed range, its voice-settings
   * knobs. `GET /api/aiprompts/dubbing-capabilities?modelIds=a,b`. Nothing on
   * the client calls it yet; the popover will, to say how a speaker's voice can
   * be directed. Same REST idiom as `getDubManifest` — typed `DubbingApiError`,
   * abort-aware, `extractErrorInfo` log.
   */
  /**
   * PLAT-12029 — write delivery markers into one line for the person, from
   * the markers the line's model documents and the note when there is one.
   * The server refuses (422) an answer that changed the words or used a
   * marker the model does not document, and (400) a model with no markers;
   * both reach the caller as a typed `DubbingApiError`. Nothing is stored.
   */
  async autoTag(params: {
    text: string;
    modelId: string;
    note?: string;
    signal?: AbortSignal;
  }): Promise<AutoTagResult> {
    const { signal, ...body } = params;
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${AUTO_TAG_ENDPOINT}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        },
      );
      await throwIfNotOk(response, 'DubbingRepository.autoTag');
      const data = (await response.json()) as unknown;
      const taggedText = (data as { taggedText?: unknown })?.taggedText;
      if (typeof taggedText !== 'string') {
        throw new Error(
          'DubbingRepository.autoTag: response missing taggedText',
        );
      }
      const markers = (data as { markers?: unknown }).markers;
      return {
        taggedText,
        markers: Array.isArray(markers)
          ? markers.filter((m): m is string => typeof m === 'string')
          : [],
        modelId: String(
          (data as { modelId?: unknown }).modelId ?? params.modelId,
        ),
      };
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('autoTag failed', {
        modelId: params.modelId,
        textLength: params.text.length,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }

  async getDubCapabilities(params: {
    modelIds: string[];
    signal?: AbortSignal;
  }): Promise<DubCapabilitiesResult> {
    const { modelIds, signal } = params;
    const query = new URLSearchParams({ modelIds: modelIds.join(',') });
    try {
      const response = await authenticatedFetch(
        `${getApiBase()}${CAPABILITIES_ENDPOINT}?${query}`,
        { method: 'GET', signal },
      );
      await throwIfNotOk(response, 'DubbingRepository.getDubCapabilities');
      const data: unknown = await response.json();
      return ensureDubCapabilities(
        data,
        'DubbingRepository.getDubCapabilities',
      );
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('getDubCapabilities failed', {
        modelIds,
        ...extractErrorInfo(error),
      });
      throw error;
    }
  }
}
